import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { API, getToken } from "@/lib/api";
import { analytics } from "@/lib/analytics";

export const RANGES = [
  { id: "24h", label: "Last 24 hours", from: () => dayjs().subtract(24, "hour") },
  { id: "7d", label: "Last 7 days", from: () => dayjs().subtract(7, "day") },
  { id: "30d", label: "Last 30 days", from: () => dayjs().subtract(30, "day") },
  { id: "90d", label: "Last 90 days", from: () => dayjs().subtract(90, "day") },
  { id: "all", label: "All time", from: () => null },
];

export const useRange = (initial = "7d") => {
  const [range, setRange] = useState(initial);
  const params = useMemo(() => {
    const r = RANGES.find((x) => x.id === range);
    const from = r?.from();
    return from ? { date_from: from.toISOString() } : {};
  }, [range]);
  return { range, setRange, params };
};

export const RangeSelect = ({ range, setRange }) => (
  <select className="tc-select" value={range} onChange={(e) => { setRange(e.target.value); analytics.track("report_range_changed", { category: "reporting", label: e.target.value }); }} data-testid="report-range-select">
    {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
  </select>
);

export const downloadCsv = async (params) => {
  analytics.track("report_export_clicked", { category: "reporting", metadata: params });
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  const res = await fetch(`${API}/reports/export.csv?${qs}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) { toast.error("Export failed"); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `total-chat-events-${dayjs().format("YYYYMMDD-HHmmss")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success("CSV downloaded");
};

export const downloadFile = async (url, filename, label = "File") => {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) { toast.error("Export failed"); return; }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
  toast.success(`${label} downloaded`);
};

export const ExportButton = ({ params }) => (
  <button className="tc-btn tc-btn--outline sm" onClick={() => downloadCsv(params)} data-testid="export-csv-button"><Download size={14} /> Export CSV</button>
);

export const num = (n) => (n ?? 0).toLocaleString();
export const mins = (n) => (n == null ? "—" : n >= 60 ? `${Math.floor(n / 60)}h ${Math.round(n % 60)}m` : `${Math.round(n * 10) / 10}m`);
export const dash = (v, fmt = (x) => x) => (v == null ? "—" : fmt(v));
